import { useState, useEffect } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Select, Input, Button, Spin } from "antd";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";
import api from "../../../../../../core/axios/axiosInstance";

const COLOR = "#ec4899";

const extractParams = (text = "") => {
  const matches = [...text.matchAll(/\{\{(\w+)\}\}/g)];
  const seen = new Set();
  return matches.map((m) => m[1]).filter((p) => { if (seen.has(p)) return false; seen.add(p); return true; });
};

const AUTO_MAP = {
  first_name: "{{firstname}}", firstname: "{{firstname}}", fname: "{{firstname}}", name: "{{firstname}}",
  last_name:  "{{lastname}}",  lastname:  "{{lastname}}",  lname: "{{lastname}}",
  full_name:  "{{firstname}}", fullname:  "{{firstname}}",
  email:      "{{email}}",
  phone:      "{{phone}}",     mobile:    "{{phone}}",     contact: "{{phone}}",
  company:    "{{company}}",   business:  "{{company}}",
  designation:"{{designation}}",title:    "{{designation}}",
};
const autoValue = (paramName) => AUTO_MAP[paramName.toLowerCase()] || "";

const isDynamicUrl = (url = "") => /\{\{.*?\}\}/.test(url);

const TemplateNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();

  const [templates, setTemplates]     = useState([]);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const [selectedTpl, setSelectedTpl] = useState(data.templateId || null);
  // Local state for inputs — prevents cursor-jump on every keystroke
  const [localBody, setLocalBody]     = useState(() => {
    const m = {};
    (data.bodyParams || []).forEach(p => { m[p.paramName] = p.value || ""; });
    return m;
  });
  const [localBtn, setLocalBtn]       = useState(() => {
    const m = {};
    (data.buttonParams || []).forEach(p => { m[p.buttonIndex] = { ...p }; });
    return m;
  });

  useEffect(() => {
    setLoadingTpl(true);
    api.get("/api/whatsapp/approved-templates")
      .then((res) => setTemplates(res.data?.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTpl(false));
  }, []);

  const currentTemplate = templates.find((t) => t._id === selectedTpl);
  const bodyComp    = currentTemplate?.components?.find((c) => c.type === "BODY");
  const headerComp  = currentTemplate?.components?.find((c) => c.type === "HEADER");
  const footerComp  = currentTemplate?.components?.find((c) => c.type === "FOOTER");
  const buttonsComp = currentTemplate?.components?.find((c) => c.type === "BUTTONS");
  const paramNames  = extractParams(bodyComp?.text || "");
  const headerIsMedia = headerComp && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format?.toUpperCase());

  const buildInitialButtonParams = (tpl) => {
    const btns = tpl?.components?.find((c) => c.type === "BUTTONS")?.buttons || [];
    return btns.map((btn, idx) => {
      const existing = (data.buttonParams || []).find((p) => p.buttonIndex === idx);
      const base = { buttonIndex: idx, type: btn.type };
      if (btn.type === "COPY_CODE")  return { ...base, couponCode: existing?.couponCode || btn.example || "" };
      if (btn.type === "URL" && isDynamicUrl(btn.url)) return { ...base, urlSuffix: existing?.urlSuffix || "" };
      if (btn.type === "FLOW") return { ...base, flowToken: existing?.flowToken || "", flowActionData: existing?.flowActionData || "" };
      return base;
    });
  };

  const onSelectTemplate = (tplId) => {
    setSelectedTpl(tplId);
    const tpl = templates.find((t) => t._id === tplId);
    if (!tpl) return;
    const body   = tpl.components?.find((c) => c.type === "BODY");
    const hdr    = tpl.components?.find((c) => c.type === "HEADER");
    const params = extractParams(body?.text || "");
    const newBodyParams = params.map((pName) => {
      const existing = (data.bodyParams || []).find((p) => p.paramName === pName);
      return { paramName: pName, value: existing?.value || autoValue(pName) };
    });
    const newButtonParams = buildInitialButtonParams(tpl);
    // Reset local mirrors
    const newLocalBody = {};
    newBodyParams.forEach(p => { newLocalBody[p.paramName] = p.value; });
    setLocalBody(newLocalBody);
    const newLocalBtn = {};
    newButtonParams.forEach(p => { newLocalBtn[p.buttonIndex] = { ...p }; });
    setLocalBtn(newLocalBtn);
    data.onChange?.({
      ...data,
      templateId:     tpl._id,
      templateName:   tpl.name,
      language:       tpl.language || "en_US",
      headerType:     hdr?.format || null,
      headerMediaUrl: (hdr && ["IMAGE","VIDEO","DOCUMENT"].includes(hdr.format?.toUpperCase()))
                        ? (hdr.media?.s3Url || "") : null,
      bodyParams:    newBodyParams,
      buttonParams:  newButtonParams,
    });
  };

  // Only flush to parent on blur — prevents cursor-jump on every keystroke
  const onParamLocalChange = (paramName, value) => {
    setLocalBody(prev => ({ ...prev, [paramName]: value }));
  };
  const onParamBlur = (paramName) => {
    const value = localBody[paramName] ?? "";
    const next = (data.bodyParams || []).map((p) =>
      p.paramName === paramName ? { ...p, value } : p
    );
    data.onChange?.({ ...data, bodyParams: next });
  };

  const onButtonLocalChange = (buttonIndex, patch) => {
    setLocalBtn(prev => ({ ...prev, [buttonIndex]: { ...(prev[buttonIndex] || {}), ...patch } }));
  };
  const onButtonBlur = (buttonIndex) => {
    const localEntry = localBtn[buttonIndex] || {};
    const existing = (data.buttonParams || []).find(p => p.buttonIndex === buttonIndex);
    const next = existing
      ? (data.buttonParams || []).map(p => p.buttonIndex === buttonIndex ? { ...p, ...localEntry } : p)
      : [...(data.buttonParams || []), { buttonIndex, ...localEntry }];
    data.onChange?.({ ...data, buttonParams: next });
  };

  const [localHeaderUrl, setLocalHeaderUrl] = useState(data.headerMediaUrl || "");
  const onHeaderMediaUrlChange = (url) => setLocalHeaderUrl(url);
  const onHeaderMediaUrlBlur   = () => data.onChange?.({ ...data, headerMediaUrl: localHeaderUrl });

  const renderButtonInput = (btn, idx) => {
    const local = localBtn[idx] || {};

    if (btn.type === "PHONE_NUMBER") return (
      <div style={{ fontSize: 10, color: "#6b7280" }}>
        📞 <strong>{btn.text}</strong> — {btn.phone_number} (no input needed)
      </div>
    );

    if (btn.type === "URL" && !isDynamicUrl(btn.url)) return (
      <div style={{ fontSize: 10, color: "#6b7280" }}>
        🔗 <strong>{btn.text}</strong> — {btn.url} (static, no input needed)
      </div>
    );

    if (btn.type === "URL" && isDynamicUrl(btn.url)) return (
      <div>
        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 3 }}>
          🔗 <strong>{btn.text}</strong> — URL suffix
        </div>
        <Input
          size="small"
          placeholder={`e.g. ${btn.url?.replace(/\{\{.*?\}\}/g, "{{phone}}")}`}
          value={local.urlSuffix ?? ""}
          onChange={(e) => onButtonLocalChange(idx, { urlSuffix: e.target.value })}
          onBlur={() => onButtonBlur(idx)}
          style={{ fontSize: 11 }}
        />
      </div>
    );

    if (btn.type === "QUICK_REPLY") return (
      <div style={{ fontSize: 10, color: "#6b7280" }}>
        ↩️ <strong>{btn.text}</strong> (quick reply, no input needed)
      </div>
    );

    if (btn.type === "COPY_CODE") return (
      <div>
        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 3 }}>
          📋 <strong>{btn.text || "Copy Code"}</strong> — coupon code
        </div>
        <Input
          size="small"
          placeholder="e.g. SAVE20 or {{coupon}}"
          value={local.couponCode ?? ""}
          onChange={(e) => onButtonLocalChange(idx, { couponCode: e.target.value })}
          onBlur={() => onButtonBlur(idx)}
          style={{ fontSize: 11 }}
        />
      </div>
    );

    if (btn.type === "FLOW") return (
      <div>
        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 3 }}>
          ⚡ <strong>{btn.text || "Open Flow"}</strong>
        </div>
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>Flow Token (leave blank to auto-generate)</div>
          <Input
            size="small"
            placeholder="auto-generated if blank"
            value={local.flowToken ?? ""}
            onChange={(e) => onButtonLocalChange(idx, { flowToken: e.target.value })}
            onBlur={() => onButtonBlur(idx)}
            style={{ fontSize: 11 }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>Pre-fill Data (optional JSON)</div>
          <Input.TextArea
            size="small"
            rows={2}
            placeholder={'{"name":"{{firstname}}"}'}
            value={local.flowActionData ?? ""}
            onChange={(e) => onButtonLocalChange(idx, { flowActionData: e.target.value })}
            onBlur={() => onButtonBlur(idx)}
            style={{ fontSize: 11 }}
          />
        </div>
      </div>
    );

    return null;
  };

  const needsInput = (btn) =>
    btn.type === "COPY_CODE" ||
    btn.type === "FLOW" ||
    (btn.type === "URL" && isDynamicUrl(btn.url));

  const buttonsThatNeedInput = (buttonsComp?.buttons || [])
    .map((btn, idx) => ({ btn, idx }))
    .filter(({ btn }) => needsInput(btn));


  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 280,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={260} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>🗂️</span>
        <strong style={{ fontSize: 13 }}>Template</strong>
        <Button type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }} />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />

        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Select approved template
        </label>
        {loadingTpl ? (
          <Spin size="small" />
        ) : (
          <Select
            style={{ width: "100%", marginBottom: 10 }}
            size="small"
            placeholder="Choose a template..."
            value={selectedTpl || undefined}
            onChange={onSelectTemplate}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={templates.map((t) => ({
              value: t._id,
              label: `${t.name} (${t.language})`,
            }))}
          />
        )}

        {/* Template preview — WhatsApp bubble */}
        {currentTemplate && (() => {
          const BTN_META = {
            PHONE_NUMBER: { icon: "📞", color: "#10b981", bg: "#f0fdf4", label: "Call" },
            URL:          { icon: "🔗", color: "#3b82f6", bg: "#eff6ff", label: "URL" },
            COPY_CODE:    { icon: "📋", color: "#f59e0b", bg: "#fffbeb", label: "Copy Code" },
            QUICK_REPLY:  { icon: "↩️", color: "#8b5cf6", bg: "#faf5ff", label: "Reply" },
            FLOW:         { icon: "⚡", color: "#ec4899", bg: "#fdf2f8", label: "Flow" },
          };
          return (
            <div style={{ marginBottom: 10 }}>
              {/* Bubble */}
              <div style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}>
                <div style={{ padding: "8px 10px" }}>
                  {headerComp?.text && (
                    <div style={{ fontWeight: 700, fontSize: 11, color: "#111827", marginBottom: 4 }}>
                      {headerComp.text}
                    </div>
                  )}
                  {headerIsMedia && (
                    <div style={{
                      background: "#f3f4f6", borderRadius: 6, padding: "6px 8px",
                      fontSize: 10, color: "#6b7280", marginBottom: 4,
                      display: "flex", alignItems: "center", gap: 5,
                    }}>
                      {headerComp.format === "IMAGE" ? "🖼️" : headerComp.format === "VIDEO" ? "🎥" : "📄"}
                      {headerComp.format} header
                    </div>
                  )}
                  {bodyComp?.text && (
                    <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                      {bodyComp.text}
                    </div>
                  )}
                  {footerComp?.text && (
                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
                      {footerComp.text}
                    </div>
                  )}
                </div>

                {/* Buttons row */}
                {(buttonsComp?.buttons || []).length > 0 && (
                  <div style={{ borderTop: "1px solid #e5e7eb" }}>
                    {buttonsComp.buttons.map((btn, i) => {
                      const meta = BTN_META[btn.type] || { icon: "▸", color: "#6b7280", bg: "#f9fafb", label: btn.type };
                      return (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "6px 10px",
                          borderBottom: i < buttonsComp.buttons.length - 1 ? "1px solid #f3f4f6" : "none",
                          background: meta.bg,
                        }}>
                          <span style={{ fontSize: 11 }}>{meta.icon}</span>
                          <span style={{ fontSize: 11, color: meta.color, fontWeight: 600, flex: 1 }}>
                            {btn.text || btn.url || meta.label}
                          </span>
                          <span style={{
                            fontSize: 9, color: meta.color,
                            background: `${meta.color}18`,
                            border: `1px solid ${meta.color}40`,
                            borderRadius: 4, padding: "1px 5px",
                            fontWeight: 600, letterSpacing: 0.3,
                          }}>
                            {btn.type.replace("_", " ")}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Body parameters */}
        {paramNames.length > 0 && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
              Parameters — use <code style={{ fontSize: 10 }}>{"{{firstname}}"}</code> for variables
            </label>
            {paramNames.map((pName) => (
              <div key={pName} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: "#6b7280", minWidth: 90, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {`{{${pName}}}`}
                </span>
                <Input
                  size="small"
                  placeholder={`Value for ${pName}`}
                  value={localBody[pName] ?? ""}
                  onChange={(e) => onParamLocalChange(pName, e.target.value)}
                  onBlur={() => onParamBlur(pName)}
                  style={{ flex: 1, fontSize: 11 }}
                />
              </div>
            ))}
          </>
        )}

        {/* Button parameters — inputs required */}
        {buttonsThatNeedInput.length > 0 && (() => {
          const TYPE_COLOR = {
            COPY_CODE: { color: "#f59e0b", bg: "#fffbeb", border: "#fcd34d" },
            FLOW:      { color: "#ec4899", bg: "#fdf2f8", border: "#f9a8d4" },
            URL:       { color: "#3b82f6", bg: "#eff6ff", border: "#93c5fd" },
          };
          return (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Button Parameters
              </div>
              {buttonsThatNeedInput.map(({ btn, idx }) => {
                const tc = TYPE_COLOR[btn.type] || { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" };
                return (
                  <div key={idx} style={{
                    background: tc.bg,
                    border: `1px solid ${tc.border}`,
                    borderLeft: `3px solid ${tc.color}`,
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 6,
                  }}>
                    {renderButtonInput(btn, idx)}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Header media URL */}
        {headerIsMedia && (
          <>
            <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4, marginTop: 4 }}>
              {headerComp.format} URL (overrides template default)
            </label>
            <Input
              size="small"
              placeholder="https://..."
              value={localHeaderUrl}
              onChange={(e) => onHeaderMediaUrlChange(e.target.value)}
              onBlur={onHeaderMediaUrlBlur}
              style={{ fontSize: 11, marginBottom: 4 }}
            />
          </>
        )}

        {data.flowTriggerType === "newCompanyAdmin" ? (
          <div style={{ marginTop: 6, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 5, padding: "5px 8px", fontSize: 10, color: "#166534", lineHeight: 1.7 }}>
            <strong>Registration vars:</strong>{" "}
            <code>{"{{reg_firstname}}"}</code> <code>{"{{reg_email}}"}</code> <code>{"{{reg_phone}}"}</code>
          </div>
        ) : (
          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>
            Sends template → advances to next node
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
    </div>
  );
};

export default TemplateNode;
